import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import type { CombinedQuerySuggestion } from '../../../@types/QuerySuggestion';
import QueryFilter from '../../../helpers/query-filter';
import { useCombinedSuggestion } from '../../../hooks/useSuggestion';
import type { RootState } from '../../../reducers';
import { requestStatus } from '../../../shared/statuses';
import Loader from '../../components/Loader';
import RichSearch from '../../components/RichSearch';
import usePrettyHistory from './usePrettyHistory';

interface MainSearchProps {
  queryFilter?: QueryFilter; //Apply query to already existing QueryFilter
}

const MainSearch = ({ queryFilter }: MainSearchProps) => {
  const { t } = useTranslation('common');
  const timeout = useRef<NodeJS.Timeout>(undefined);
  const navigate = useNavigate();
  const [querySuggestionQuery, setQuerySuggestionQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [suggestionList, setSuggestionList] = useState<undefined | CombinedQuerySuggestion>();
  const hitCount = useSelector<RootState, number | undefined>((s) => s.search.hitCount);
  const loadingFacets = useSelector<RootState, boolean>(
    (s) => s.search.requestStatus === requestStatus.FETCHING_LIST,
  );
  const [query, setQuery] = useState('');
  const [suggestions, request] = useCombinedSuggestion(querySuggestionQuery);
  const history = usePrettyHistory();

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.currentTarget.value);

  const onClick = () => {
    const qf = queryFilter
      ? new QueryFilter(queryFilter.toString()).setQuery(query).toQueryString()
      : QueryFilter.byQuery(query).toQueryString();

    setQuery('');
    setQuerySuggestionQuery('');
    navigate(`/sok${qf}`);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && focusedIndex === null) {
      setQuery('');
      setQuerySuggestionQuery('');
      onClick();
    }
  };

  const onSuggestionClick = () => {
    setQuery('');
    setQuerySuggestionQuery('');
  };

  const onClear = () => {
    setQuery('');
    setQuerySuggestionQuery('');
  };

  useEffect(() => {
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      setQuerySuggestionQuery(query);
    }, 200);
  }, [query]);

  useEffect(() => {
    if (!request.isLoading) {
      setSuggestionList(suggestions);
    }
  }, [suggestions, request.isLoading]);

  return (
    <RichSearch
      placeholder={t('Search', { ns: 'common' }) || undefined}
      value={query}
      meta={
        (!queryFilter?.toString() && hitCount) || !loadingFacets ? (
          t('Number of cars', { ns: 'common', count: hitCount || 0 })
        ) : (
          <Loader inline />
        )
      }
      history={history}
      suggestions={suggestionList}
      queryFilter={queryFilter}
      focusedIndex={focusedIndex}
      inputTestId="vehicle-search-input"
      buttonTestId="vehicle-search-button"
      metaTestId="vehicle-search-counter"
      onChange={onChange}
      onKeyDown={onKeyDown}
      onClick={onClick}
      onClear={onClear}
      onSuggestionClick={onSuggestionClick}
      onSetFocusedIndex={setFocusedIndex}
    />
  );
};

export default MainSearch;
